// KillSwitchPanel — lifted out of the LiveTrading component body (God 2026-08-26,
// phase 4). L3 global kill: stop everything, everywhere, now.
//
// Phases 1-3 moved top-level declarations, which close over nothing. This block
// lived INSIDE the component and closed over its state, so it could not be moved
// verbatim — it needed a prop for every name it borrowed. That prop list was
// computed by intersecting the block's identifiers with the component's scope
// (tools/refactor_livetrading_phase4.py), not written by hand: a hand-listed
// prop set is exactly where a missed dependency becomes a runtime undefined that
// the compiler never sees.
//
// Every prop keeps its ORIGINAL LOCAL NAME, so the JSX below is the original
// text. Behaviour is unchanged by construction.

import React from 'react';
import { API_BASE_URL, pCol } from './foundation';
import { ExecControl } from '../components/ui/ExecControl';

export interface KillSwitchPanelProps {
  addLog: any;
  backendOpenTrades: any;
  backendSummary: any;
  deployments: any;
  killSwitchActive: any;
  setDeployments: any;
  setKillSwitchActive: any;
  setMasterSwitch: any;
  setShowKillPanel: any;
  showKillPanel: any;
  showToast: any;
  socketRef: any;
}

export const KillSwitchPanel: React.FC<KillSwitchPanelProps> = ({ addLog, backendOpenTrades, backendSummary, deployments, killSwitchActive, setDeployments, setKillSwitchActive, setMasterSwitch, setShowKillPanel, showKillPanel, showToast, socketRef }) => (
  <>
        {/* ✅ v9.0 — GLOBAL KILL SWITCH PANEL */}
        {showKillPanel && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.93)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:999999,padding:20}}>
            <div style={{background:'linear-gradient(160deg,#1a0000,#0a0000)',border:'2px solid #ef4444',borderRadius:16,padding:'min(32px, 6vw)',width:'min(500px, calc(100vw - 24px))',
              /* M-H2: a short phone must not cut the buttons off */
              maxHeight:'88dvh',overflowY:'auto',overscrollBehavior:'contain',
              textAlign:'center',boxShadow:'0 0 80px rgba(239,68,68,.5)'}}>
              <div style={{fontSize:52,marginBottom:12}}>🛑</div>
              <div style={{fontSize:22,fontWeight:900,color:killSwitchActive?'#ef4444':'#fbbf24',letterSpacing:2,marginBottom:8}}>
                {killSwitchActive ? 'KILL SWITCH ACTIVE' : 'RISK MANAGER'}
              </div>
              <div style={{fontSize:11,color:'#94a3b8',marginBottom:20,lineHeight:1.8}}>
                {killSwitchActive ? (
                  <span>Sare bots band ho gaye hain. Open positions square-off ho rahi hain. Aaj ke liye trading band hai. Kal 9:15 AM par auto-reset hoga.</span>
                ) : (
                  <span>Is button se manually Kill Switch activate karo. Sare bots turant band honge aur positions square-off hongi.</span>
                )}
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:20}}>
                {[
                  {label:'TODAY PnL', val:`${(backendSummary?.total_pnl||0)>=0?'+':''}₹${Math.abs(backendSummary?.total_pnl||0).toLocaleString('en-IN')}`, col:pCol(backendSummary?.total_pnl||0)},
                  {label:'MDD LIMIT', val:'₹10,000', col:'#fbbf24'},
                  {label:'RUNNING BOTS', val:String(deployments.filter(d=>d.status==='RUNNING').length), col:'#60a5fa'},
                  {label:'OPEN POSITIONS', val:String(backendOpenTrades.length), col:'#a855f7'},
                ].map((s,i) => (
                  <div key={i} style={{background:'rgba(0,0,0,.4)',border:'1px solid #7f1d1d',borderRadius:10,padding:'12px 0'}}>
                    <div style={{fontSize:8,color:'#6b7280',letterSpacing:1.5,textTransform:'uppercase',marginBottom:4}}>{s.label}</div>
                    <div style={{fontSize:20,fontWeight:900,color:s.col,fontFamily:'monospace'}}>{s.val}</div>
                  </div>
                ))}
              </div>
              <div style={{display:'flex',gap:10}}>
                {!killSwitchActive && (
                  /* M-H2 (God D25): risk-off still fires on a SHORT hold (0.9 s), never a stray tap */
                  <ExecControl tone="neg" style={{flex:1}} onConfirm={()=>{
                    if (socketRef.current) socketRef.current.emit('manual_kill_switch', {});
                    setKillSwitchActive(true);
                    setMasterSwitch(false);
                    setDeployments(p => p.map(d => ({...d, status:'STOPPED' as const})));
                    addLog('KILL_SWITCH','🚨 Manual kill switch triggered by admin','ERROR');
                    showToast('🚨 KILL SWITCH ACTIVATED!');
                  }}>
                    🚨 HOLD · ACTIVATE KILL SWITCH
                  </ExecControl>
                )}
                {killSwitchActive && (
                  /* M-H2: reset re-opens trading — the LIVE-length hold (1.4 s) */
                  <ExecControl live tone="pos" onConfirm={async ()=>{
                    try {
                      await fetch(`${API_BASE_URL}/kill-switch/reset`, {
                        method:'POST', headers:{'Content-Type':'application/json'},
                        credentials: 'include',
                      });
                    } catch {}
                    setKillSwitchActive(false);
                    setShowKillPanel(false);
                    addLog('KILL_SWITCH','🟢 Kill Switch reset by admin — bots abhi bhi STOPPED hain, master alag se ARM karo','SUCCESS');
                    showToast('🟢 Kill reset — ab MASTER arm karke bots START karo (auto re-arm NAHI hota)');
                  }}>
                    🔄 HOLD · RESET & RESUME
                  </ExecControl>
                )}
                <button onClick={()=>setShowKillPanel(false)} className="lt-btn" style={{minHeight:48,padding:'14px 20px',background:'#1e293b',color:'#94a3b8',border:'1px solid #334155',fontSize:11}}>
                  CLOSE
                </button>
              </div>
            </div>
          </div>
        )}
  </>
);
